import React from 'react';

import BaseFormObject, { FieldsType } from 'jsx/utils/form/baseMobxFormObject';
import NullableProperties from 'jsx/types/utils/nullableProperties';

type FieldValueType = string | null | boolean | number;

export interface FormFieldType<Value extends FieldValueType> {
  id: string;
  label: string;
  name: string;
  value: Value;
  onBlur: (event: any) => any;
  onChange: (event: any) => any;
  onFocus: (event: any) => any;
  set: (propOrValue: any, value?: any) => void;
  reset: () => void;
  touched: boolean;
  blurred: boolean;
  focused: boolean;
  hasError: boolean;
  error: string | null;
  checked: boolean | null;
  placeholder: string;
  rules: string | null;
  autoFocus: boolean;
  disabled: boolean;
  isValid: boolean;
  type: string;
  bind: (newProps?: { [key: string]: any }) => {
    autoFocus: boolean;
    checked: boolean | undefined;
    disabled: boolean;
    id: string;
    label: string;
    name: string;
    onBlur: (event: any) => any;
    onChange: (event: any) => any;
    onFocus: (event: any) => any;
    placeholder: string;
    type: string;
    value: any;
  };
  $changed: boolean;
}

interface FieldValues {
  [key: string]: FieldValueType;
}

export interface InjectedForm<Fields extends FieldValues> {
  $: <FieldName extends keyof Fields>(
    name: FieldName,
  ) => FormFieldType<Fields[typeof name]>;
  update: (fields: NullableProperties<Partial<Fields>>) => void;
  onError: (form?: this) => any;
  onSuccess?: ((form: this) => any) | null;
  onSubmit: (event?: React.FormEvent) => any;
  values: () => Fields;
  reset: () => void;
  handleReset: () => void;
  has: (name: string) => boolean;
  add: (field: { name: string; [key: string]: any }) => void;
  del: (name: string) => void;
  changed: boolean;
  isDirty: boolean;
  isValid: boolean;
  hasError: boolean;
  touched: boolean;
  observe: <Path extends keyof Fields>(data: {
    path: Path;
    call: (formObserver: FormObserver<Fields, Path>) => void;
  }) => void;
  dispose: () => void;
}

export interface FormObserver<
  Fields extends FieldValues,
  Path extends keyof Fields,
> {
  field: FormFieldType<Fields[Path]>;
  form: InjectedForm<Fields>;
  change: Record<keyof Fields, FieldValueType>;
  path: Path;
}

export const withValidation = <Props extends {}, Fields extends FieldsType>(
  Component: React.ComponentType<Props>,
  fields: Fields,
  Form = BaseFormObject,
) => {
  const form = new Form({ fields });

  const ValidatedForm = (props: Omit<Props, 'form'>) => (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <Component {...(props as Props)} form={form} />
  );

  return ValidatedForm;
};
